import {
  MovedState,
  TechRadarLoaderResponse,
} from '@backstage-community/plugin-tech-radar-common';
import { PolicyStatus, Technology, VersionPolicy } from './types';

const RINGS = [
  {
    id: 'adopt',
    name: 'ADOPT',
    color: '#5BA300',
    description: 'Soportado — usar por defecto en proyectos nuevos.',
  },
  {
    id: 'trial',
    name: 'TRIAL',
    color: '#009EB0',
    description:
      'Migrar al tocar — válido, pero migrar cuando se toque el código.',
  },
  {
    id: 'assess',
    name: 'ASSESS',
    color: '#C7BA00',
    description: 'Deprecated — planificar migración; evitar en trabajo nuevo.',
  },
  {
    id: 'hold',
    name: 'HOLD',
    color: '#E09B96',
    description: 'Prohibido — no usar; migrar de inmediato.',
  },
] as const;

const QUADRANTS = [
  { id: 'languages', name: 'Languages' },
  { id: 'frameworks', name: 'Frameworks' },
  { id: 'platforms', name: 'Platforms' },
  { id: 'tools', name: 'Tools' },
] as const;

function statusToRingId(status: PolicyStatus): string | undefined {
  switch (status) {
    case 'supported':
      return 'adopt';
    case 'migrate_on_touch':
      return 'trial';
    case 'deprecated':
      return 'assess';
    case 'banned':
      return 'hold';
    default:
      return undefined;
  }
}

function quadrantFor(tech: Technology): string {
  const haystack = `${tech.key} ${tech.name}`.toLowerCase();
  if (
    /python|java\b|golang|\bgo\b|node|typescript|javascript|ruby|php|rust|kotlin|scala|csharp|c#|\.net|swift/.test(
      haystack,
    )
  ) {
    return 'languages';
  }
  if (
    /react|fastapi|nestjs|spring|django|flask|express|next|vue|angular|rails|laravel|quarkus|gin|fiber/.test(
      haystack,
    )
  ) {
    return 'frameworks';
  }
  if (
    /postgres|mysql|redis|mongo|kafka|rabbit|docker|k8s|kubernetes|aws|gcp|azure|nginx|elastic|opensearch|railway/.test(
      haystack,
    )
  ) {
    return 'platforms';
  }
  return 'tools';
}

/** Build a Thoughtworks-style radar payload from Stack Policies data. */
export function buildRadarFromPolicies(
  technologies: Technology[],
  policies: VersionPolicy[],
): TechRadarLoaderResponse {
  const techById = new Map(technologies.map(t => [t.id, t]));

  const entries = policies
    .map(policy => {
      const tech = techById.get(policy.technologyId);
      if (!tech) return undefined;
      const ringId = statusToRingId(policy.status);
      if (!ringId) return undefined;

      const title = `${tech.name} ${policy.version}`.trim();
      const id = policy.id;
      return {
        key: id,
        id,
        title,
        quadrant: quadrantFor(tech),
        description:
          policy.message ||
          `${tech.name} ${policy.version} — política Stack Policies (${policy.status}).`,
        timeline: [
          {
            date: new Date(policy.updatedAt || policy.createdAt),
            ringId,
            description: policy.message || undefined,
            moved: MovedState.NoChange,
          },
        ],
      };
    })
    .filter((e): e is NonNullable<typeof e> => Boolean(e));

  return {
    rings: RINGS.map(r => ({ ...r })),
    quadrants: QUADRANTS.map(q => ({ ...q })),
    entries,
  };
}
